import React from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse, IMInputText, IMSelect } from '@insurancemenu/imenu-components-lib';
import { IMSicSearch } from '@insurancemenu/imenu-components-lib/dist/components/imForm/capabilities/sic/IMSicSearch';
import SectionTitle from 'components/common/general/SectionTitle';
import DomainProductLines from 'components/common/initiate-proposal/DomainProductLines';
import {
  IMFormStyled,
  FollowingSectionTitleStyled,
  IMZipCodeFieldStyled,
  IMZipCountyFieldStyled,
  IMZipStateFieldStyled,
} from 'components/common/initiate-proposal/styles';
import { SunLifeInitiateProposalFormDelegate } from 'store/pages/initiate-quote/sunLife/proposalFormDelegate';
import { INCORPORATION_DATE_GUIDELINES } from 'global/utils/sponsor-validations/sunLife/incorporationDateValidation';
import { AlertStyled, IncorporationDateAlertStyled, SICWrapperStyled } from './styles';
import {
  SUN_LIFE_INCORPORATION_DATE_ALERT_TEST_ID,
  SUN_LIFE_INITIATE_PROPOSAL_FORM_ID,
  SUN_LIFE_SIC_WARNING_ALERT_TEST_ID,
} from './constants';

interface Props {
  proposalFormDelegate: SunLifeInitiateProposalFormDelegate;
}

const SunLifeInitiateProposalForm: React.FC<Props> = observer(({ proposalFormDelegate }) => {
  const {
    proposalForm,
    effectiveDatesOptions,
    domainProductLineItems,
    isDomainProductLinesLoading,
    sicWarningMessage,
  } = proposalFormDelegate;

  const showWarning = Boolean(sicWarningMessage);

  return (
    <IMFormStyled id={SUN_LIFE_INITIATE_PROPOSAL_FORM_ID} formController={proposalForm}>
      <SectionTitle title="Basic Information" />
      <IMInputText label="Proposal Name" name="proposalName" size="full" />
      <IMSelect label="Effective Date" name="effectiveDate" size="full" options={effectiveDatesOptions} />
      <IMZipCodeFieldStyled size="s" />
      <IMZipStateFieldStyled size="s" />
      <IMZipCountyFieldStyled size="s" />

      <FollowingSectionTitleStyled title="Additional Information" />
      <IncorporationDateAlertStyled
        content={[
          'Group must have been in business for a minimum of 12 months prior to the requested policy effective date.',
        ]}
        title="Note"
        type="warning"
        data-test-id={SUN_LIFE_INCORPORATION_DATE_ALERT_TEST_ID}
      />
      <IMInputText
        label="Incorporation Date"
        name="incorporationDate"
        size="full"
        htmlType="date"
        additionalOptions={{
          guidelines: INCORPORATION_DATE_GUIDELINES,
          showGuidelinesByDefault: true,
        }}
      />
      <IMInputText label="Estimated Number of Eligible Employees" name="groupSize" size="full" />
      <SICWrapperStyled>
        <IMSicSearch label="SIC Code" size="full" />
        <Collapse show={showWarning}>
          <AlertStyled
            content={[sicWarningMessage]}
            title="Note"
            type="warning"
            data-test-id={SUN_LIFE_SIC_WARNING_ALERT_TEST_ID}
          />
        </Collapse>
      </SICWrapperStyled>

      <FollowingSectionTitleStyled title="Available Product Lines" />
      <DomainProductLines domainProductLineItems={domainProductLineItems} isLoading={isDomainProductLinesLoading} />
    </IMFormStyled>
  );
});

export default SunLifeInitiateProposalForm;
